import { inject, shallowRef } from 'vue'
import type { AccessRole, GetAccessRolesRequestParams } from '@gat-ship/types'
import {
  accessRolesCollectionKey,
  type AccessRolesCollectionService
} from '@gat-ship/types/injection-keys'
import { useAccessControlSignalREvents } from '@gat-ship/signalr'
import { globalUtils } from '@gat-ship/utils'
import { AccessRoleDataBuilder } from '../model/access-role-data-builder'
import {
  containsAccessRoleId,
  findAccessRoleById,
  removeAccessRoleById,
  replaceAccessRoleById,
  sortAccessRolesByName
} from '../model/access-role-collection'
import { useAccessControlApiCalls } from './useAccessControlApiCalls'

/**
 * Data source for the collection of all access roles (`AccessRole[]`).
 *
 * App-section-scoped and shared across features (custom-fields,
 * tenant-users). For a single role's permissions/users detail, use
 * {@link useAccessRoleDetail} instead.
 */
export function useAccessRolesCollection(): AccessRolesCollectionService {
  const {
    getAccessRolesRequest,
    getAccessRoleRequest,
    changeAccessRoleNameRequest
  } = useAccessControlApiCalls()

  const _accessRoles = shallowRef<AccessRole[]>([])

  function _setAccessRoles(accessRoles: AccessRole[]) {
    _accessRoles.value = sortAccessRolesByName(accessRoles)
  }

  function _updateAccessRole(updatedAccessRole: AccessRole) {
    _setAccessRoles(
      replaceAccessRoleById(_accessRoles.value, updatedAccessRole)
    )
  }

  function addAccessRole(accessRole: AccessRole) {
    _setAccessRoles([..._accessRoles.value, accessRole])
  }

  function removeAccessRole(id: string) {
    _setAccessRoles(removeAccessRoleById(_accessRoles.value, id))
  }

  async function changeAccessRoleName(
    roleId: string,
    name: string,
    description: string
  ): Promise<AccessRole | undefined> {
    const accessRole = findAccessRoleById(_accessRoles.value, roleId)
    if (accessRole) {
      const orgAccessRole = globalUtils.functions.duplicateViaJson(accessRole)
      const updatedAccessRole: AccessRole = {
        ...accessRole,
        name,
        description
      }
      try {
        _updateAccessRole(updatedAccessRole)
        await changeAccessRoleNameRequest({
          roleId,
          payload: {
            name,
            description
          },
          commandId: 'change-access-role-name'
        })
        return updatedAccessRole
      } catch (error) {
        _updateAccessRole(orgAccessRole)
        throw error
      }
    }
    return undefined
  }

  const signalREvents = useAccessControlSignalREvents({
    onAccessRoleCreated: (payload) => {
      const accessRole = AccessRoleDataBuilder.createAccessRole({
        id: payload.message.roleId,
        name: payload.message.name,
        description: payload.message.description
      })
      addAccessRole(accessRole)
    },
    onAccessRoleDeleted: (payload) => {
      removeAccessRole(payload.message.roleId)
    },
    onAccessRoleNameChanged: async (payload) => {
      const { roleId } = payload.message
      if (containsAccessRoleId(_accessRoles.value, roleId)) {
        const result = await getAccessRoleRequest({ roleId })
        _updateAccessRole(result.role)
      }
    }
  })

  function subscribeToAccessRolesEvents() {
    signalREvents.subscribeToAccessControlEvents()
    return signalREvents.unsubscribeFromAccessControlEvents
  }

  function unsubscribeFromAccessRolesEvents() {
    signalREvents.unsubscribeFromAccessControlEvents()
  }

  /**
   * Fetches the roles. If the roles are already loaded, it will not be fetched again unless forceFetch is true.
   * @param options.forceFetch - Whether to force a fetch of the roles. @default false
   * @param options.subscribeToEvents - Whether to subscribe to role events. @default true
   */
  async function fetchAccessRoles(
    params?: GetAccessRolesRequestParams & {
      forceFetch?: boolean
      subscribeToEvents?: boolean
    }
  ) {
    if (_accessRoles.value.length === 0 || params?.forceFetch) {
      const result = await getAccessRolesRequest(params)
      _setAccessRoles(result.roles)
      const subscribeToEvents = params?.subscribeToEvents ?? true
      if (subscribeToEvents) {
        signalREvents.subscribeToAccessControlEvents()
      }
      return result
    }
    return undefined
  }

  // Bound to a local before returning so the extra `signalREvents` member
  // (a `@gat-ship/signalr` value-type, intentionally absent from the
  // runtime-free `AccessRolesCollectionService` contract) is allowed through
  // structural assignability instead of tripping excess-property checks.
  const service = {
    accessRoles: _accessRoles,
    addAccessRole,
    changeAccessRoleName,
    removeAccessRole,
    clearData: (options?: { unsubscribeFromEvents?: boolean }) => {
      _setAccessRoles([])
      if (options?.unsubscribeFromEvents) {
        signalREvents.unsubscribeFromAccessControlEvents()
      }
    },
    fetchAccessRoles,
    signalREvents,
    subscribeToAccessRolesEvents,
    unsubscribeFromAccessRolesEvents
  }
  return service
}

/** Inject the access-roles collection context provided by a parent provider. */
export function useAccessRolesCollectionContext() {
  return inject(accessRolesCollectionKey, undefined)
}
